import React, { useEffect, useMemo, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import Loading from '../../components/Loading';
import DataTable from 'react-data-table-component';
import client from '../../graphql/client';
import { safeGraphQLQuery } from '../../graphql/api/userApi';
import { GET_ALL_REWARD_DETAILS } from '../../graphql/RewardsGQL';
import './RewardPage.css';

// =====================================================
// TYPES
// =====================================================

interface LoyaltyIdDetails {
  id: string;
  idCode: string;
  userId: string;
  source: string;
  createdAt: string;
}

interface LoyaltyQueueDetails {
  id: string;
  userId: string;
  queuePosition: number;
  joinDate: string;
  rejoinCount: number;
  isActive: boolean;
}

interface RewardDetails {
  userId: string;
  idFund: number;
  walletBalance: number;
  totalEarned: number;
  totalUsed: number;
  totalLoyaltyIds: number;
  activeQueueEntries: number;
  loyaltyIds: LoyaltyIdDetails[];
  queueEntries: LoyaltyQueueDetails[];
}

// =====================================================
// PAGE
// =====================================================

const RewardPage: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const [rewardDetails, setRewardDetails] =
    useState<RewardDetails[]>([]);

  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState('');

  const [activeTab, setActiveTab] = useState<
    'overview' | 'customers' | 'loyalty' | 'queue'
  >('overview');

  // ===================================================
  // FETCH ALL REWARD DETAILS
  // ===================================================

  const fetchRewardDetails = async () => {
    try {
      setLoading(true);

      console.log('Fetching all reward details...');

      const data = await safeGraphQLQuery<{
        getAllRewardDetails: RewardDetails[];
      }>(
        client,
        GET_ALL_REWARD_DETAILS
      );

      console.log(
  'GET_ALL_REWARD_DETAILS RESPONSE:',
  JSON.stringify(data, null, 2)
);

console.log(
  'REWARD RECORD COUNT:',
  data?.getAllRewardDetails?.length
);

      setRewardDetails(
        data?.getAllRewardDetails || []
      );
    } catch (error: any) {
      console.error(
        'GET_ALL_REWARD_DETAILS ERROR:',
        error
      );

      alert(
        error?.message ||
        'Failed to load reward details'
      );

      setRewardDetails([]);
    } finally {
      setLoading(false);
    }
  };

  // ===================================================
  // LOAD ALL RECORDS WHEN PAGE OPENS
  // ===================================================

  useEffect(() => {
    fetchRewardDetails();
  }, []);

  // ===================================================
  // HELPERS
  // ===================================================

  const formatAmount = (
    amount?: number | null
  ) => {
    return `₹${Number(amount || 0).toFixed(2)}`;
  };

  const formatDate = (
    date?: string | null
  ) => {
    if (!date) {
      return '-';
    }

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return date;
    }

    return parsed.toLocaleString();
  };

  // ===================================================
  // ALL LOYALTY IDS
  // ===================================================

  const allLoyaltyIds = useMemo(() => {
    return rewardDetails.flatMap(
      (item) => item.loyaltyIds || []
    );
  }, [rewardDetails]);

  // ===================================================
  // ALL QUEUE RECORDS
  // ===================================================

  const allQueueEntries = useMemo(() => {
    return rewardDetails.flatMap(
      (item) => item.queueEntries || []
    );
  }, [rewardDetails]);

  // ===================================================
  // SEARCH CUSTOMERS
  // ===================================================

  const filteredCustomers = useMemo(() => {
    const search = searchText
      .toLowerCase()
      .trim();

    if (!search) {
      return rewardDetails;
    }

    return rewardDetails.filter(
      (item) =>
        item.userId
          ?.toLowerCase()
          .includes(search)
    );
  }, [rewardDetails, searchText]);

  // ===================================================
  // SEARCH LOYALTY IDS
  // ===================================================

  const filteredLoyaltyIds = useMemo(() => {
    const search = searchText
      .toLowerCase()
      .trim();

    if (!search) {
      return allLoyaltyIds;
    }

    return allLoyaltyIds.filter(
      (item) =>
        item.userId
          ?.toLowerCase()
          .includes(search) ||
        item.idCode
          ?.toLowerCase()
          .includes(search) ||
        item.source
          ?.toLowerCase()
          .includes(search) ||
        item.id
          ?.toLowerCase()
          .includes(search)
    );
  }, [allLoyaltyIds, searchText]);

  // ===================================================
  // SEARCH QUEUE
  // ===================================================

  const filteredQueue = useMemo(() => {
    const search = searchText
      .toLowerCase()
      .trim();

    if (!search) {
      return allQueueEntries;
    }

    return allQueueEntries.filter(
      (item) =>
        item.userId
          ?.toLowerCase()
          .includes(search) ||
        item.id
          ?.toLowerCase()
          .includes(search) ||
        String(item.queuePosition)
          .includes(search) ||
        String(item.rejoinCount)
          .includes(search)
    );
  }, [allQueueEntries, searchText]);

  // ===================================================
  // TOTAL ID FUND
  // ===================================================

  const totalIdFund = useMemo(() => {
    return rewardDetails.reduce(
      (sum, item) =>
        sum + Number(item.idFund || 0),
      0
    );
  }, [rewardDetails]);

  // ===================================================
  // TOTAL WALLET BALANCE
  // ===================================================

  const totalWalletBalance = useMemo(() => {
    return rewardDetails.reduce(
      (sum, item) =>
        sum + Number(item.walletBalance || 0),
      0
    );
  }, [rewardDetails]);

  // ===================================================
  // TOTAL EARNED
  // ===================================================

  const totalEarned = useMemo(() => {
    return rewardDetails.reduce(
      (sum, item) =>
        sum + Number(item.totalEarned || 0),
      0
    );
  }, [rewardDetails]);

  // ===================================================
  // TOTAL USED
  // ===================================================

  const totalUsed = useMemo(() => {
    return rewardDetails.reduce(
      (sum, item) =>
        sum + Number(item.totalUsed || 0),
      0
    );
  }, [rewardDetails]);

  // ===================================================
  // TOTAL LOYALTY IDS
  // ===================================================

  const totalLoyaltyIds = useMemo(() => {
    return rewardDetails.reduce(
      (sum, item) =>
        sum + Number(item.totalLoyaltyIds || 0),
      0
    );
  }, [rewardDetails]);

  // ===================================================
  // ACTIVE QUEUE
  // ===================================================

  const totalActiveQueueEntries = useMemo(() => {
    return rewardDetails.reduce(
      (sum, item) =>
        sum + Number(item.activeQueueEntries || 0),
      0
    );
  }, [rewardDetails]);

  // ===================================================
  // CUSTOMER TABLE
  // ===================================================

  const customerColumns = [
    {
      name: 'User ID',
      selector: (row: RewardDetails) =>
        row.userId || '-',
      sortable: true,
      grow: 3,
    },

    {
      name: 'ID Fund',
      selector: (row: RewardDetails) =>
        formatAmount(row.idFund),
      sortable: true,
      right: true,
    },

    {
      name: 'Wallet Balance',
      selector: (row: RewardDetails) =>
        formatAmount(row.walletBalance),
      sortable: true,
      right: true,
    },

    {
      name: 'Total Earned',
      selector: (row: RewardDetails) =>
        formatAmount(row.totalEarned),
      sortable: true,
      right: true,
    },

    {
      name: 'Total Used',
      selector: (row: RewardDetails) =>
        formatAmount(row.totalUsed),
      sortable: true,
      right: true,
    },

    {
      name: 'Loyalty IDs',
      selector: (row: RewardDetails) =>
        row.totalLoyaltyIds,
      sortable: true,
      center: true,
    },

    {
      name: 'Active Queue',
      selector: (row: RewardDetails) =>
        row.activeQueueEntries,
      sortable: true,
      center: true,
    },
  ];

  // ===================================================
  // LOYALTY ID TABLE
  // ===================================================

  const loyaltyColumns = [
    {
      name: 'Loyalty ID',
      selector: (row: LoyaltyIdDetails) =>
        row.idCode || '-',
      sortable: true,
      grow: 2,
    },

    {
      name: 'User ID',
      selector: (row: LoyaltyIdDetails) =>
        row.userId || '-',
      sortable: true,
      grow: 3,
    },

    {
      name: 'Source',
      cell: (row: LoyaltyIdDetails) => (
        <span className="reward_badge reward_badge_parent">
          {row.source || '-'}
        </span>
      ),
      sortable: true,
    },

    {
      name: 'Loyalty Record ID',
      selector: (row: LoyaltyIdDetails) =>
        row.id || '-',
      sortable: true,
      grow: 2,
    },

    {
      name: 'Created At',
      selector: (row: LoyaltyIdDetails) =>
        formatDate(row.createdAt),
      sortable: true,
    },
  ];

  // ===================================================
  // QUEUE TABLE
  // ===================================================

  const queueColumns = [
    {
      name: 'Queue Position',
      selector: (row: LoyaltyQueueDetails) =>
        row.queuePosition,
      sortable: true,
      center: true,
    },

    {
      name: 'User ID',
      selector: (row: LoyaltyQueueDetails) =>
        row.userId || '-',
      sortable: true,
      grow: 3,
    },

    {
      name: 'Status',
      cell: (row: LoyaltyQueueDetails) => (
        <span
          className={
            row.isActive
              ? 'reward_badge reward_badge_active'
              : 'reward_badge reward_badge_inactive'
          }
        >
          {row.isActive
            ? 'Active'
            : 'Inactive'}
        </span>
      ),
      sortable: true,
    },

    {
      name: 'Rejoin Count',
      selector: (row: LoyaltyQueueDetails) =>
        row.rejoinCount,
      sortable: true,
      center: true,
    },

    {
      name: 'Join Date',
      selector: (row: LoyaltyQueueDetails) =>
        formatDate(row.joinDate),
      sortable: true,
    },

    {
      name: 'Queue Record',
      selector: (row: LoyaltyQueueDetails) =>
        row.id || '-',
      sortable: true,
      grow: 2,
    },
  ];

  // ===================================================
  // LOADING
  // ===================================================

  if (loading) {
    return (
      <div className="reward_loading_page">
        <Loading />
      </div>
    );
  }

  // ===================================================
  // PAGE
  // ===================================================

  return (
    <div className="reward_page">

      {/* SIDEBAR */}

      <Sidebar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
      />

      {/* MAIN */}

      <main
        className={`reward_main ${
          isSidebarOpen
            ? 'reward_sidebar_open'
            : 'reward_sidebar_closed'
        }`}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="reward_header">

          <div>

            <div className="reward_breadcrumb">
              Admin / Rewards
            </div>

            <h1>
              LNG Reward Management
            </h1>

            <p>
              All customers, Loyalty IDs, queue
              and reward wallet details
            </p>

          </div>

          <button
            className="reward_refresh_button"
            onClick={fetchRewardDetails}
          >
            ↻ Refresh
          </button>

        </div>

        {/* =================================================
            SEARCH
        ================================================= */}

        <div className="reward_filter_card">

          <div className="reward_filter_group">

            <label>
              Search All Records
            </label>

            <input
              type="text"
              value={searchText}
              onChange={(e) =>
                setSearchText(e.target.value)
              }
              placeholder="Search User ID, Loyalty ID, queue..."
            />

          </div>

        </div>

        {/* =================================================
            NO DATA
        ================================================= */}

        {rewardDetails.length === 0 ? (

          <div className="reward_table_card">

            <div className="reward_empty">

              <h3>
                No Reward Details Found
              </h3>

              <p>
                No reward information is currently
                available.
              </p>

            </div>

          </div>

        ) : (

          <>

            {/* =================================================
                SUMMARY CARDS
            ================================================= */}

            <section className="reward_summary_grid">

              <div className="reward_summary_card">

                <div className="reward_summary_icon">
                  ₹
                </div>

                <div>

                  <span>
                    Total ID Fund
                  </span>

                  <strong>
                    {formatAmount(totalIdFund)}
                  </strong>

                </div>

              </div>

              <div className="reward_summary_card">

                <div className="reward_summary_icon">
                  ₹
                </div>

                <div>

                  <span>
                    Total Wallet Balance
                  </span>

                  <strong>
                    {formatAmount(
                      totalWalletBalance
                    )}
                  </strong>

                </div>

              </div>

              <div className="reward_summary_card">

                <div className="reward_summary_icon">
                  +
                </div>

                <div>

                  <span>
                    Total Earned
                  </span>

                  <strong>
                    {formatAmount(totalEarned)}
                  </strong>

                </div>

              </div>

              <div className="reward_summary_card">

                <div className="reward_summary_icon">
                  −
                </div>

                <div>

                  <span>
                    Total Used
                  </span>

                  <strong>
                    {formatAmount(totalUsed)}
                  </strong>

                </div>

              </div>

            </section>

            {/* =================================================
                SECONDARY SUMMARY
            ================================================= */}

            <div className="reward_overview_grid">

              <div className="reward_info_card">

                <span>
                  Total Customers
                </span>

                <strong>
                  {rewardDetails.length}
                </strong>

              </div>

              <div className="reward_info_card">

                <span>
                  Total Loyalty IDs
                </span>

                <strong>
                  {totalLoyaltyIds}
                </strong>

              </div>

              <div className="reward_info_card">

                <span>
                  Active Queue Entries
                </span>

                <strong>
                  {totalActiveQueueEntries}
                </strong>

              </div>

              <div className="reward_info_card">

                <span>
                  Pool Target
                </span>

                <strong>
                  ₹300.00
                </strong>

              </div>

            </div>

            {/* =================================================
                TABS
            ================================================= */}

            <div className="reward_tabs">

              <button
                className={
                  activeTab === 'overview'
                    ? 'reward_tab active'
                    : 'reward_tab'
                }
                onClick={() =>
                  setActiveTab('overview')
                }
              >
                Overview
              </button>

              <button
                className={
                  activeTab === 'customers'
                    ? 'reward_tab active'
                    : 'reward_tab'
                }
                onClick={() =>
                  setActiveTab('customers')
                }
              >
                Customers
              </button>

              <button
                className={
                  activeTab === 'loyalty'
                    ? 'reward_tab active'
                    : 'reward_tab'
                }
                onClick={() =>
                  setActiveTab('loyalty')
                }
              >
                Loyalty IDs
              </button>

              <button
                className={
                  activeTab === 'queue'
                    ? 'reward_tab active'
                    : 'reward_tab'
                }
                onClick={() =>
                  setActiveTab('queue')
                }
              >
                Queue
              </button>

            </div>

            {/* =================================================
                OVERVIEW
            ================================================= */}

            {activeTab === 'overview' && (

              <section className="reward_overview">

                <div className="reward_overview_card">

                  <div className="reward_section_title">

                    <h2>
                      LNG Reward Flow
                    </h2>

                    <span>
                      New LNG System
                    </span>

                  </div>

                  <div className="reward_flow">

                    <div className="reward_flow_item">

                      <div className="reward_flow_number">
                        1
                      </div>

                      <div>

                        <strong>
                          ID Fund
                        </strong>

                        <p>
                          Total current ID fund:
                          {' '}
                          {formatAmount(totalIdFund)}
                        </p>

                      </div>

                    </div>

                    <div className="reward_flow_line" />

                    <div className="reward_flow_item">

                      <div className="reward_flow_number">
                        2
                      </div>

                      <div>

                        <strong>
                          ₹300 Threshold
                        </strong>

                        <p>
                          Loyalty ID is created
                          when the required fund
                          reaches the threshold.
                        </p>

                      </div>

                    </div>

                    <div className="reward_flow_line" />

                    <div className="reward_flow_item">

                      <div className="reward_flow_number">
                        3
                      </div>

                      <div>

                        <strong>
                          Loyalty IDs
                        </strong>

                        <p>
                          Total created:
                          {' '}
                          {totalLoyaltyIds}
                        </p>

                      </div>

                    </div>

                    <div className="reward_flow_line" />

                    <div className="reward_flow_item">

                      <div className="reward_flow_number">
                        4
                      </div>

                      <div>

                        <strong>
                          Queue
                        </strong>

                        <p>
                          Active entries:
                          {' '}
                          {totalActiveQueueEntries}
                        </p>

                      </div>

                    </div>

                  </div>

                </div>

              </section>

            )}

            {/* =================================================
                CUSTOMERS
            ================================================= */}

            {activeTab === 'customers' && (

              <section className="reward_table_card">

                <div className="reward_table_header">

                  <div>

                    <h2>
                      All Customers
                    </h2>

                    <p>
                      Reward details for every
                      customer
                    </p>

                  </div>

                  <span className="reward_count">

                    {filteredCustomers.length}
                    {' '}
                    records

                  </span>

                </div>

                {filteredCustomers.length === 0 ? (

                  <div className="reward_empty">

                    <h3>
                      No Customers
                    </h3>

                    <p>
                      No matching customer records
                      were found.
                    </p>

                  </div>

                ) : (

                  <DataTable
                    columns={customerColumns}
                    data={filteredCustomers}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    persistTableHead
                  />

                )}

              </section>

            )}

            {/* =================================================
                LOYALTY IDS
            ================================================= */}

            {activeTab === 'loyalty' && (

              <section className="reward_table_card">

                <div className="reward_table_header">

                  <div>

                    <h2>
                      All Loyalty IDs
                    </h2>

                    <p>
                      All Loyalty IDs generated
                      across customers
                    </p>

                  </div>

                  <span className="reward_count">

                    {filteredLoyaltyIds.length}
                    {' '}
                    records

                  </span>

                </div>

                {filteredLoyaltyIds.length === 0 ? (

                  <div className="reward_empty">

                    <h3>
                      No Loyalty IDs
                    </h3>

                    <p>
                      No Loyalty IDs have been
                      created.
                    </p>

                  </div>

                ) : (

                  <DataTable
                    columns={loyaltyColumns}
                    data={filteredLoyaltyIds}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    persistTableHead
                  />

                )}

              </section>

            )}

            {/* =================================================
                QUEUE
            ================================================= */}

            {activeTab === 'queue' && (

              <section className="reward_table_card">

                <div className="reward_table_header">

                  <div>

                    <h2>
                      All Loyalty Queue
                    </h2>

                    <p>
                      Queue positions and rejoin
                      information for all customers
                    </p>

                  </div>

                  <span className="reward_count">

                    {filteredQueue.length}
                    {' '}
                    records

                  </span>

                </div>

                {filteredQueue.length === 0 ? (

                  <div className="reward_empty">

                    <h3>
                      No Queue Records
                    </h3>

                    <p>
                      No queue records are
                      available.
                    </p>

                  </div>

                ) : (

                  <DataTable
                    columns={queueColumns}
                    data={filteredQueue}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    persistTableHead
                  />

                )}

              </section>

            )}

          </>

        )}

      </main>

    </div>
  );
};

export default RewardPage;